import React from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const AdminSettings = () => {
  const { resetToDefaults } = useAdmin();

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings? This will remove all custom content, images, and layout changes.')) {
      resetToDefaults();
    }
  };

  const exportData = () => {
    const data = {
      siteContent: localStorage.getItem('naqd-site-content'),
      blogPosts: localStorage.getItem('naqd-blog-posts'),
      layoutConfig: localStorage.getItem('naqd-layout-config'),
      images: localStorage.getItem('naqd-images'),
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `naqd-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const importData = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          
          if (data.siteContent) localStorage.setItem('naqd-site-content', data.siteContent);
          if (data.blogPosts) localStorage.setItem('naqd-blog-posts', data.blogPosts);
          if (data.layoutConfig) localStorage.setItem('naqd-layout-config', data.layoutConfig);
          if (data.images) localStorage.setItem('naqd-images', data.images);
          
          alert('Data imported successfully! The page will reload to apply changes.');
          window.location.reload();
        } catch (error) {
          alert('Error importing data. Please check the file format.');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAllData = () => {
    if (window.confirm('This will permanently delete ALL custom data. This cannot be undone. Are you sure?')) {
      localStorage.removeItem('naqd-site-content');
      localStorage.removeItem('naqd-blog-posts');
      localStorage.removeItem('naqd-layout-config');
      localStorage.removeItem('naqd-images');
      alert('All data cleared! The page will reload.');
      window.location.reload();
    }
  };

  return (
    <div className="section-padding">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-naqd-light-text dark:text-naqd-dark-text mb-2">
              Admin Settings
            </h1>
            <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
              Manage your website data, export/import settings, and reset to defaults
            </p>
          </div>

          {/* Data Management */}
          <div className="card mb-8">
            <h2 className="text-xl font-semibold text-naqd-light-text dark:text-naqd-dark-text mb-4">
              Data Management
            </h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-naqd-light-text dark:text-naqd-dark-text mb-2">
                  Export Data
                </h3>
                <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-3">
                  Download all your custom content, blog posts, layout settings, and images as a backup file.
                </p>
                <button
                  onClick={exportData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Export Backup
                </button>
              </div>

              <div>
                <h3 className="text-lg font-medium text-naqd-light-text dark:text-naqd-dark-text mb-2">
                  Import Data
                </h3>
                <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-3">
                  Restore your website data from a previous backup file.
                </p>
                <input
                  type="file"
                  accept=".json"
                  onChange={importData}
                  className="block w-full text-sm text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>
          </div>

          {/* Reset Options */}
          <div className="card border-l-4 border-yellow-400 mb-8">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-naqd-light-text dark:text-naqd-dark-text mb-2">
                  Reset Options
                </h2>
                <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-4">
                  Use these options carefully as they will permanently remove your custom data.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-naqd-light-text dark:text-naqd-dark-text mb-2">
                      Reset to Defaults
                    </h3>
                    <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-3">
                      Restore all content, layout, and settings to the original default values.
                    </p>
                    <button
                      onClick={handleReset}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center"
                    >
                      <ArrowPathIcon className="w-4 h-4 mr-2" />
                      Reset All to Defaults
                    </button>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-naqd-light-text dark:text-naqd-dark-text mb-2">
                      Clear All Data
                    </h3>
                    <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-3">
                      Permanently delete all custom content, images, blog posts, and layout settings.
                    </p>
                    <button
                      onClick={clearAllData}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Clear All Data
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Storage Info */}
          <div className="card">
            <h2 className="text-xl font-semibold text-naqd-light-text dark:text-naqd-dark-text mb-4">
              Storage Information
            </h2>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">Site Content:</span>
                <span className="text-naqd-light-text dark:text-naqd-dark-text">
                  {Math.round(JSON.stringify(localStorage.getItem('naqd-site-content') || '').length / 1024)} KB
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">Blog Posts:</span>
                <span className="text-naqd-light-text dark:text-naqd-dark-text">
                  {Math.round(JSON.stringify(localStorage.getItem('naqd-blog-posts') || '').length / 1024)} KB
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">Layout Config:</span>
                <span className="text-naqd-light-text dark:text-naqd-dark-text">
                  {Math.round(JSON.stringify(localStorage.getItem('naqd-layout-config') || '').length / 1024)} KB
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">Images:</span>
                <span className="text-naqd-light-text dark:text-naqd-dark-text">
                  {Math.round(JSON.stringify(localStorage.getItem('naqd-images') || '').length / 1024)} KB
                </span>
              </div>
            </div>
          </div>

          {/* Developer Info */}
          <div className="mt-8 text-sm text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
            <p>
              All data is stored locally in your browser's localStorage. 
              This means your changes are saved on this device only and won't affect other users.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;